import {
  Briefcase,
  Calendar,
  FileText,
  IndianRupee,
  Link as LinkIcon,
  MapPin,
  User,
  X,
} from 'lucide-react';

export const ViewJobModal = (props) => {
  const { isOpen, onClose, job } = props;

  if (!isOpen || !job) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'Inactive':
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      case 'Closed':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'Public':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'Private':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto'>
      <div className='bg-card rounded-lg shadow-xl w-full max-w-4xl border border-border my-8 max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border bg-card'>
          <h3 className='text-lg font-semibold text-foreground'>Job Details</h3>
          <button
            onClick={onClose}
            className='text-muted-foreground hover:text-foreground transition-colors'
            type='button'
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* Title Section */}
          <div className='mb-6 pb-6 border-b border-border'>
            <div className='flex items-start gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center'>
                <Briefcase size={24} className='text-primary-foreground' />
              </div>
              <div className='flex-1'>
                <h4 className='text-2xl font-bold text-foreground mb-2'>
                  {job.jobTitle}
                </h4>
                <div className='flex flex-wrap items-center gap-3'>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(job.status)}`}
                  >
                    {job.status}
                  </span>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full border ${getVisibilityColor(job.visibility)}`}
                  >
                    {job.visibility}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Basic Information */}
            <div className='space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>
                Basic Information
              </h5>

              <div className='space-y-3'>
                <div className='flex items-start gap-3'>
                  <IndianRupee
                    size={18}
                    className='text-muted-foreground mt-0.5 flex-shrink-0'
                  />
                  <div>
                    <p className='text-xs text-muted-foreground'>
                      Compensation
                    </p>
                    <p className='text-sm text-foreground'>
                      {job.compensation || 'Not specified'}{' '}
                      {job.compensation && `(${job.currency})`}
                    </p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <User
                    size={18}
                    className='text-muted-foreground mt-0.5 flex-shrink-0'
                  />
                  <div>
                    <p className='text-xs text-muted-foreground'>Posted By</p>
                    <p className='text-sm text-foreground'>
                      {job.sellerId?.firstName && job.sellerId?.lastName
                        ? `${job.sellerId.firstName} ${job.sellerId.lastName}`
                        : 'Unknown'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {job.sellerId?.email || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className='space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>
                Application Details
              </h5>

              <div className='space-y-3'>
                <div className='flex items-start gap-3'>
                  <Calendar
                    size={18}
                    className='text-muted-foreground mt-0.5 flex-shrink-0'
                  />
                  <div>
                    <p className='text-xs text-muted-foreground'>
                      Application Deadline
                    </p>
                    <p className='text-sm text-foreground'>
                      {job.applicationDeadline
                        ? new Date(job.applicationDeadline).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            },
                          )
                        : 'N/A'}
                    </p>
                    {job.applicationDeadlineTime && (
                      <p className='text-xs text-muted-foreground'>
                        {job.applicationDeadlineTime}
                      </p>
                    )}
                  </div>
                </div>

                {job.applicationLink && (
                  <div className='flex items-start gap-3'>
                    <LinkIcon
                      size={18}
                      className='text-muted-foreground mt-0.5 flex-shrink-0'
                    />
                    <div>
                      <p className='text-xs text-muted-foreground'>
                        Application Link
                      </p>
                      <a
                        href={job.applicationLink}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-sm text-primary hover:underline break-all'
                      >
                        {job.applicationLink}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            {(job.address?.city ||
              job.address?.state ||
              job.address?.country) && (
              <div className='md:col-span-2 space-y-4'>
                <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2'>
                  <MapPin size={16} />
                  Location
                </h5>
                <div className='p-4 bg-blue rounded-lg'>
                  {job.address.addressLine1 && (
                    <p className='text-sm text-foreground text-white'>
                      {job.address.addressLine1}
                    </p>
                  )}
                  {job.address.addressLine2 && (
                    <p className='text-sm text-foreground text-white'>
                      {job.address.addressLine2}
                    </p>
                  )}
                  {job.address.landmark && (
                    <p className='text-sm text-muted-foreground text-white'>
                      Near: {job.address.landmark}
                    </p>
                  )}
                  <p className='text-sm text-foreground mt-1 text-white'>
                    {[
                      job.address.city,
                      job.address.state,
                      job.address.pincode,
                      job.address.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Description */}
            {job.description && (
              <div className='md:col-span-2 space-y-4'>
                <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2'>
                  <FileText size={16} />
                  Description
                </h5>
                <div className='p-4 bg-blue rounded-lg'>
                  <p className='text-sm text-foreground whitespace-pre-wrap text-white'>
                    {job.description}
                  </p>
                </div>
              </div>
            )}

            {/* Requirements */}
            {job.requirement && (
              <div className='md:col-span-2 space-y-4'>
                <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2'>
                  <FileText size={16} />
                  Requirements
                </h5>
                <div className='p-4 bg-blue rounded-lg'>
                  <p className='text-sm text-foreground whitespace-pre-wrap text-white'>
                    {job.requirement}
                  </p>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className='md:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-border'>
              <div className='flex items-start gap-3'>
                <Calendar
                  size={18}
                  className='text-muted-foreground mt-0.5 flex-shrink-0'
                />
                <div>
                  <p className='text-xs text-muted-foreground'>Created At</p>
                  <p className='text-sm text-foreground'>
                    {formatDate(job.createdAt)}
                  </p>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <Calendar
                  size={18}
                  className='text-muted-foreground mt-0.5 flex-shrink-0'
                />
                <div>
                  <p className='text-xs text-muted-foreground'>Updated At</p>
                  <p className='text-sm text-foreground'>
                    {formatDate(job.updatedAt)}
                  </p>
                </div>
              </div>

              {job.createdBy && (
                <div className='flex items-start gap-3'>
                  <User
                    size={18}
                    className='text-muted-foreground mt-0.5 flex-shrink-0'
                  />
                  <div>
                    <p className='text-xs text-muted-foreground'>Created By</p>
                    <p className='text-sm text-foreground'>
                      {job.createdBy.firstName} {job.createdBy.lastName}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='sticky bottom-0 px-6 py-4 border-t border-border bg-card'>
          <button
            onClick={onClose}
            className='w-full px-4 py-2 bg-primary rounded-lg text-white'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
